import React from "react";
import { Link, useSearch } from "wouter";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { motion } from "framer-motion";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { CheckCircle2, Clock, ArrowRight, UserPlus } from "lucide-react";
import { getExperienceTailwindClass } from "@/lib/utils";
import {
  useGetMe,
  useGetProfile,
  getGetMeQueryKey,
  getGetProfileQueryKey,
} from "@workspace/api-client-react";

export default function ConfirmationPage() {
  useDocumentTitle("Profile Submitted");
  const isReduced = useReducedMotion();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const optimisticVerified = searchParams.get("verified") === "true";

  const { data: me, isLoading: meLoading } = useGetMe({
    query: {
      queryKey: getGetMeQueryKey(),
      retry: false,
    },
  });

  const profileId = me?.profileId ?? null;

  const safeProfileId = profileId ?? 0;
  const { data: profile } = useGetProfile(safeProfileId, {
    query: {
      queryKey: getGetProfileQueryKey(safeProfileId),
      enabled: profileId !== null,
      refetchOnMount: true,
      staleTime: 0,
    },
  });

  const isVerified = profile !== undefined ? profile.verified : optimisticVerified;
  const profileName = profile?.name ?? me?.displayName ?? null;

  if (meLoading) {
    return (
      <div className="flex-1 flex items-center justify-center pt-24 pb-20">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (me && profileId === null) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-[0.03] pointer-events-none" />
        <motion.div
          className="max-w-md w-full bg-zinc-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center relative z-10 shadow-2xl"
          initial={isReduced ? false : { opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: isReduced ? 0 : 0.5, ease: "easeOut" }}
        >
          <motion.div
            initial={isReduced ? false : { scale: 0 }}
            animate={{ scale: 1 }}
            transition={isReduced ? { duration: 0 } : { delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
            className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6"
            style={{ backgroundColor: "rgba(225, 29, 72, 0.1)", color: "#e11d48" }}
          >
            <UserPlus className="w-10 h-10" />
          </motion.div>
          <motion.h1
            className="text-3xl font-bold uppercase tracking-tight text-white mb-4"
            initial={isReduced ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={isReduced ? { duration: 0 } : { delay: 0.3 }}
          >
            No profile yet
          </motion.h1>
          {me.displayName && (
            <motion.p
              className="text-zinc-500 text-sm font-sans mb-2 uppercase tracking-wider"
              initial={isReduced ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={isReduced ? { duration: 0 } : { delay: 0.35 }}
            >
              {me.displayName}
            </motion.p>
          )}
          <motion.p
            className="text-zinc-400 font-sans mb-8 leading-relaxed"
            initial={isReduced ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={isReduced ? { duration: 0 } : { delay: 0.4 }}
          >
            You're logged in but haven't created your member profile yet. Complete your profile to join The Carry Network.
          </motion.p>
          <motion.div
            initial={isReduced ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={isReduced ? { duration: 0 } : { delay: 0.5 }}
          >
            <Link
              href="/onboarding"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-12 px-8 bg-primary text-white hover:bg-primary/90 font-bold uppercase tracking-wider text-sm w-full"
            >
              Complete Your Profile
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient background glows */}
      {isVerified ? (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[100px] pointer-events-none" />
      ) : (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
      )}
      
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-[0.03] pointer-events-none" />

      <motion.div 
        className="max-w-md w-full bg-zinc-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center relative z-10 shadow-2xl"
        initial={isReduced ? false : { opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: isReduced ? 0 : 0.5, ease: "easeOut" }}
      >
        <motion.div
          initial={isReduced ? false : { scale: 0 }}
          animate={{ scale: 1 }}
          transition={isReduced ? { duration: 0 } : { delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
          className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6"
          style={{
            backgroundColor: isVerified ? 'rgba(16, 185, 129, 0.1)' : 'rgba(225, 29, 72, 0.1)',
            color: isVerified ? '#10b981' : '#e11d48'
          }}
        >
          {isVerified ? (
            <CheckCircle2 className="w-10 h-10" />
          ) : (
            <Clock className="w-10 h-10" />
          )}
        </motion.div>

        <motion.h1 
          className="text-3xl font-bold uppercase tracking-tight text-white mb-4"
          initial={isReduced ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={isReduced ? { duration: 0 } : { delay: 0.3 }}
        >
          {isVerified ? "Your profile is live." : "Profile submitted."}
        </motion.h1>

        {profileName && (
          <motion.p
            className="text-zinc-500 text-sm font-sans mb-2 uppercase tracking-wider"
            initial={isReduced ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={isReduced ? { duration: 0 } : { delay: 0.35 }}
          >
            {profileName}
          </motion.p>
        )}

        {profile?.industryExperience && (
          <motion.div
            className="flex justify-center mb-4"
            initial={isReduced ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={isReduced ? { duration: 0 } : { delay: 0.38 }}
          >
            <span className={`inline-flex items-center text-xs font-semibold bg-transparent border border-current rounded px-2 py-1 ${getExperienceTailwindClass(profile.industryExperience)}`}>
              {profile.industryExperience}
            </span>
          </motion.div>
        )}

        <motion.p 
          className="text-zinc-400 font-sans mb-8 leading-relaxed"
          initial={isReduced ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={isReduced ? { duration: 0 } : { delay: 0.4 }}
        >
          {isVerified 
            ? "You're now part of The Carry Network. Your profile is visible to verified recruiters."
            : "Your profile is under review. We'll verify your Riot Games tenure and notify you when your profile goes live."
          }
        </motion.p>

        <motion.div
          initial={isReduced ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={isReduced ? { duration: 0 } : { delay: 0.5 }}
        >
          {isVerified ? (
            <Link href="/" className="inline-flex items-center justify-center whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-12 px-8 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wider text-sm w-full">
              View the Directory
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          ) : (
            <Link href="/" className="inline-flex items-center justify-center whitespace-nowrap rounded-md ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-12 px-8 bg-zinc-800 text-white hover:bg-zinc-700 font-bold uppercase tracking-wider text-sm w-full">
              Back to Home
            </Link>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
}
